import { useEffect, useState } from "react";
import { Segment, Menu, Header, Grid, Dropdown } from "semantic-ui-react";
import OrgUsersGrid from "./OrgUsersGrid";
import { OrgUser } from "../models/OrgUser";
import AddOrgUserModal from "./AddOrgUserModal";
import ResetPasswordModal from "./ResetPasswordModal";
import { toastSuccess, toastError } from "./../utils";
import { AsyncActionStatus } from "../models/AsyncActionStatus";
import { User } from "../models/User";
import { CalibrationCertificate } from "../models/CalibrationCertificate";
import { OrgUserFileInput } from "../models/globalTypes";
import EditOrgUserModalContainer from "../containers/EditOrgUserModalContainer";
import { ActiveLicence } from "../models/ActiveLicence";
import PhoneBookFilter from "./common/PhoneBookFilter";
import { sortBy } from "lodash";
import { Project } from "../models/Project";
import { AdminRole } from "../models/AdminRole";
import { LicenceType } from "../models/globalTypes";
import OrgUserViewContainer from "../containers/OrgUserViewContainer";

export type ProjectFilter = { type: "project"; projectId: string } | { type: "noProject" } | { type: "all" };

const sortByVisible = true;

const NO_PROJECT_KEY = "no_project";

interface Props {
  activeLicences?: ActiveLicence[] | null;
  isOrgUsersLoading: boolean;
  orgUsers?: OrgUser[];
  projects?: Project[] | null;
  onProjectFilter: (filter: ProjectFilter) => void;
  adminRoles?: AdminRole[];
  onAddOrgUser: (
    firstName: string,
    lastName: string,
    email: string,
    password: string,
    adminRoleId: string | null,
    phoneNumber: string,
    calibrationCertificate: CalibrationCertificate,
    orgUserFiles: OrgUserFileInput[],
    licenceId: string | null,
  ) => void;
  addUserStatus: AsyncActionStatus;
  onAddOrgUserWithExistingUser: (
    id: string,
    adminRoleId: string | null,
    phoneNumber: string,
    calibrationCertificate: CalibrationCertificate,
    orgUserFiles: OrgUserFileInput[],
    licenceId: string | null,
  ) => void;
  addUserWithExistingUserStatus: AsyncActionStatus;
  onEditOrgUser: (
    id: string,
    firstName: string,
    lastName: string,
    adminRoleId: string | null,
    phoneNumber: string,
    licenceId: string | null,
    calibrationCertificate: CalibrationCertificate,
    newOrgUserFiles: OrgUserFileInput[],
    deletedOrgUserFileIds: string[],
  ) => void;
  editUserStatus: AsyncActionStatus;
  onDeleteOrgUser: (id: string) => void;
  deleteUserStatus: AsyncActionStatus;
  onResetPassword: (id: string, newPassword: string) => void;
  resetPasswordStatus: AsyncActionStatus;
  onSearchUser: (email: string) => void;
  onSearchUserReset: () => void;
  user?: User;
  onToggleArchivedStatusOrgUser: (orgUserId: string) => void;
  toggleArchivedStatusOrgUserStatus: AsyncActionStatus;
  canAssignAdminRole: boolean;
}

const OrgUsersList = ({
  activeLicences,
  isOrgUsersLoading,
  orgUsers,
  projects,
  onProjectFilter,
  adminRoles,
  onAddOrgUser,
  addUserStatus,
  onAddOrgUserWithExistingUser,
  addUserWithExistingUserStatus,
  onEditOrgUser,
  editUserStatus,
  onDeleteOrgUser,
  deleteUserStatus,
  onResetPassword,
  resetPasswordStatus,
  onSearchUser,
  onSearchUserReset,
  user,
  onToggleArchivedStatusOrgUser,
  toggleArchivedStatusOrgUserStatus,
  canAssignAdminRole,
}: Props) => {
  const [openAddOrgUserModal, setOpenAddOrgUserModal] = useState(false);
  const [openEditOrgUserModal, setOpenEditOrgUserModal] = useState(false);
  const [openViewOrgUserSection, setOpenViewOrgUserSection] = useState(false);
  const [openResetPasswordOrgUserModal, setOpenResetPasswordOrgUserModal] = useState(false);
  const [selectedOrgUserToView, setSelectedOrgUserToView] = useState<OrgUser | undefined>(undefined);
  const [selectedOrgUserToEdit, setSelectedOrgUserToEdit] = useState<OrgUser | undefined>(undefined);
  const [selectedChar, setSelectedChar] = useState("");
  const [filteredOrgUsers, setFilteredOrgUsers] = useState<OrgUser[]>([]);
  const [sortOrder, setSortOrder] = useState<string | undefined>(undefined);
  const [selectedLicenceType, setSelectedLicenceType] = useState<string>("");

  useEffect(() => {
    if (orgUsers) {
      const charFiltered = selectedChar
        ? orgUsers.filter((u) => u.user.lastName?.toUpperCase().startsWith(selectedChar))
        : orgUsers;

      const filteredList = selectedLicenceType
        ? selectedLicenceType === "noLicence"
          ? charFiltered.filter((u) => !u.licence)
          : charFiltered.filter((u) => u.licence?.type === selectedLicenceType)
        : charFiltered;

      let sortedList: OrgUser[];
      switch (sortOrder) {
        case "lastNameDesc":
          sortedList = sortBy(filteredList, [(u) => u.user.lastName?.toLowerCase()]).reverse();
          break;
        case "lastNameAsc":
          sortedList = sortBy(filteredList, [(u) => u.user.lastName?.toLowerCase()]);
          break;
        case "firstNameAsc":
          sortedList = sortBy(filteredList, [(u) => u.user.firstName?.toLowerCase()]);
          break;
        case "firstNameDesc":
          sortedList = sortBy(filteredList, [(u) => u.user.firstName?.toLowerCase()]).reverse();
          break;
        case "lastLoginAsc":
          sortedList = sortBy(filteredList, [(u) => u.user.lastLoginDateUtc || ""]);
          break;
        case "lastLoginDesc":
          sortedList = sortBy(filteredList, [(u) => u.user.lastLoginDateUtc || ""]).reverse();
          break;
        case "statusActive":
          sortedList = sortBy(filteredList, [(u) => u.isArchived, (u) => u.user.lastName?.toLowerCase()]);
          break;
        case "statusArchived":
          sortedList = sortBy(filteredList, [(u) => !u.isArchived]);
          break;
        case "emailAsc":
          sortedList = sortBy(filteredList, [(u) => u.user.email?.toLowerCase()]);
          break;
        case "emailDesc":
          sortedList = sortBy(filteredList, [(u) => u.user.email?.toLowerCase()]).reverse();
          break;
        default:
          sortedList = filteredList;
          break;
      }

      setFilteredOrgUsers(sortedList);
    }
  }, [orgUsers, selectedChar, sortOrder, selectedLicenceType]);

  const handleEditOrgUser = (orgUserId: string) => {
    const user = orgUsers?.find((u) => u.id === orgUserId);
    if (user) {
      setSelectedOrgUserToEdit({ ...user } as OrgUser);
      setOpenEditOrgUserModal(true);
    }
  };

  const handleResetPasswordOrgUser = (orgUserId: string) => {
    const user = orgUsers?.find((u) => u.id === orgUserId);
    if (user) {
      setSelectedOrgUserToEdit({ ...user } as OrgUser);
      setOpenResetPasswordOrgUserModal(true);
    }
  };

  const handleDeleteOrgUser = (orgUserId: string) => {
    const user = orgUsers?.find((u) => u.id === orgUserId);
    onDeleteOrgUser(user?.userId!);
  };

  useEffect(() => {
    if (deleteUserStatus.successful) {
      toastSuccess("User deleted successfully!");
      handleModalsClose();
    } else if (deleteUserStatus.errors && deleteUserStatus.errors.length > 0) {
      toastError(deleteUserStatus.errors[0]);
      handleModalsClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteUserStatus]);

  const handleModalsClose = () => {
    setOpenAddOrgUserModal(false);
    setOpenEditOrgUserModal(false);
    setOpenResetPasswordOrgUserModal(false);
    setSelectedOrgUserToEdit(undefined);

    addUserStatus.reset();
    addUserWithExistingUserStatus.reset();
    editUserStatus.reset();
    deleteUserStatus.reset();
    resetPasswordStatus.reset();
    onSearchUserReset();
    toggleArchivedStatusOrgUserStatus.reset();
  };

  const handleAddOrgUserModalSubmit = (
    firstName: string,
    lastName: string,
    email: string,
    password: string,
    adminRoleId: string | null,
    phoneNumber: string,
    calibrationCertificate: CalibrationCertificate,
    orgUserFiles: OrgUserFileInput[],
    licenceId: string | null,
  ) => {
    if (user) {
      onAddOrgUserWithExistingUser(user.id, adminRoleId, phoneNumber, calibrationCertificate, orgUserFiles, licenceId);
    } else {
      onAddOrgUser(
        firstName,
        lastName,
        email,
        password,
        adminRoleId,
        phoneNumber,
        calibrationCertificate,
        orgUserFiles,
        licenceId,
      );
    }
  };

  useEffect(() => {
    if (addUserStatus.successful) {
      toastSuccess("User added successfully!");
      handleModalsClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addUserStatus.successful]);

  useEffect(() => {
    if (addUserWithExistingUserStatus.successful) {
      toastSuccess("User added successfully!");
      handleModalsClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addUserWithExistingUserStatus.successful]);

  useEffect(() => {
    if (editUserStatus.successful) {
      toastSuccess("User updated successfully!");
      setOpenEditOrgUserModal(false);
      setSelectedOrgUserToEdit(undefined);
    }
  }, [editUserStatus.successful]);

  const handleResetPasswordOrgUserModalSubmit = (newPassword: string) => {
    if (selectedOrgUserToEdit) {
      onResetPassword(selectedOrgUserToEdit.userId, newPassword);
    }
  };

  useEffect(() => {
    if (resetPasswordStatus.successful) {
      toastSuccess("Password reset successfully!");
      setOpenResetPasswordOrgUserModal(false);
      setSelectedOrgUserToEdit(undefined);
    }
  }, [resetPasswordStatus.successful]);

  useEffect(() => {
    if (toggleArchivedStatusOrgUserStatus.successful) {
      toastSuccess("User archive status changed successfully!");
      handleModalsClose();
    } else if (toggleArchivedStatusOrgUserStatus.errors) {
      toastError(toggleArchivedStatusOrgUserStatus.errors.toString());
      handleModalsClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toggleArchivedStatusOrgUserStatus]);

  const handleOrgUserClicked = (orgUser: OrgUser) => {
    setSelectedOrgUserToView(orgUser);
    setOpenViewOrgUserSection(true);
  };

  const handleViewOrgUserSectionClose = () => {
    setSelectedOrgUserToView(undefined);
    setOpenViewOrgUserSection(false);
  };

  const handleProjectFilterChange = (e: any, { value }: any) => {
    if (!value) {
      onProjectFilter({ type: "all" });
    } else if (value === NO_PROJECT_KEY) {
      onProjectFilter({ type: "noProject" });
    } else {
      onProjectFilter({ type: "project", projectId: value });
    }
  };

  const handleSortOrderChange = (e: any, { value }: any) => {
    setSortOrder(value);
  };

  const handleLicenceTypeFilterChange = (e: any, { value }: any) => {
    setSelectedLicenceType(value ?? "");
  };

  const licenceTypeOptions = [
    { key: "standard", text: "Standard", value: LicenceType.STANDARD },
    { key: "viewer", text: "Viewer", value: LicenceType.VIEWER },
    { key: "noLicence", text: "No Licence", value: "noLicence" },
  ];

  const sortOptions = [
    { key: "lastNameAsc", text: "Last Name (A-Z)", value: "lastNameAsc" },
    { key: "lastNameDesc", text: "Last Name (Z-A)", value: "lastNameDesc" },
    { key: "firstNameAsc", text: "First Name (A-Z)", value: "firstNameAsc" },
    { key: "firstNameDesc", text: "First Name (Z-A)", value: "firstNameDesc" },
    { key: "emailAsc", text: "Email (A-Z)", value: "emailAsc" },
    { key: "emailDesc", text: "Email (Z-A)", value: "emailDesc" },
    { key: "lastLoginAsc", text: "Last Login (Oldest First)", value: "lastLoginAsc" },
    { key: "lastLoginDesc", text: "Last Login (Newest First)", value: "lastLoginDesc" },
    // { key: "statusActive", text: "Status (Active First)", value: "statusActive" },
    // { key: "statusArchived", text: "Status (Archived First)", value: "statusArchived" },
    // { key: "roleAdmin", text: "Role (Admin First)", value: "roleAdmin" },
    // { key: "roleOperator", text: "Role (Operator First)", value: "roleOperator" },
  ];

  return (
    <>
      <Segment basic>
        <Header as="h2">User</Header>
      </Segment>

      <Menu pointing secondary color="green">
        <Menu.Item icon="plus" name="New User" onClick={(e) => setOpenAddOrgUserModal(true)} />

        <Menu.Menu position="right">
          {sortByVisible && (
            <Menu.Item>
              <Dropdown
                placeholder="Sort by"
                basic
                clearable
                value={sortOrder}
                onChange={handleSortOrderChange}
                options={sortOptions}
              ></Dropdown>
            </Menu.Item>
          )}
          <Menu.Item>
            <Dropdown
              placeholder="Licence Type"
              basic
              clearable
              value={selectedLicenceType || undefined}
              onChange={handleLicenceTypeFilterChange}
              options={licenceTypeOptions}
            ></Dropdown>
          </Menu.Item>
          <Menu.Item>
            <Dropdown
              placeholder="Project"
              basic
              clearable
              onChange={handleProjectFilterChange}
              options={[
                { key: NO_PROJECT_KEY, text: "No Project", value: NO_PROJECT_KEY },
                ...(projects?.map((t) => ({ key: t.id, text: t.name, value: t.id })) ?? []),
              ]}
            ></Dropdown>
          </Menu.Item>
        </Menu.Menu>
      </Menu>

      <Grid>
        <Grid.Row only="large screen">
          <Grid.Column>
            <PhoneBookFilter
              items={orgUsers || []}
              charSelector={(u: OrgUser) => u.user.lastName[0]?.toUpperCase() ?? ""}
              onSelect={(char: string) => setSelectedChar(char)}
              selectedChar={selectedChar}
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>

      <Segment basic loading={isOrgUsersLoading}>
        <Grid columns={openViewOrgUserSection ? "2" : "1"}>
          <Grid.Row>
            <Grid.Column width={openViewOrgUserSection ? "10" : "16"}>
              <OrgUsersGrid
                orgUsers={filteredOrgUsers}
                selectedOrgUserId={selectedOrgUserToView?.id}
                onEditOrgUser={handleEditOrgUser}
                onDeleteOrgUser={handleDeleteOrgUser}
                onResetPasswordOrgUser={handleResetPasswordOrgUser}
                onToggleArchivedStatusOrgUser={onToggleArchivedStatusOrgUser}
                onOrgUserClicked={handleOrgUserClicked}
              />
            </Grid.Column>
            {openViewOrgUserSection && (
              <Grid.Column width="6">
                <OrgUserViewContainer
                  onClose={handleViewOrgUserSectionClose}
                  orgUserId={selectedOrgUserToView?.id}
                ></OrgUserViewContainer>
              </Grid.Column>
            )}
          </Grid.Row>
        </Grid>
      </Segment>

      <AddOrgUserModal
        open={openAddOrgUserModal}
        onClose={handleModalsClose}
        onSubmit={handleAddOrgUserModalSubmit}
        submitInProgress={addUserStatus.loading || addUserWithExistingUserStatus.loading}
        submitErrors={addUserStatus.errors ? addUserStatus.errors : addUserWithExistingUserStatus.errors}
        onEmailEntered={onSearchUser}
        user={user}
        activeLicences={activeLicences}
        canAssignAdminRole={canAssignAdminRole}
        adminRoles={adminRoles}
      ></AddOrgUserModal>

      <EditOrgUserModalContainer
        open={openEditOrgUserModal}
        onClose={handleModalsClose}
        onSubmit={onEditOrgUser}
        submitInProgress={editUserStatus.loading}
        orgUser={selectedOrgUserToEdit}
        submitErrors={editUserStatus.errors}
        activeLicences={activeLicences}
        canAssignAdminRole={canAssignAdminRole}
        adminRoles={adminRoles}
      ></EditOrgUserModalContainer>

      <ResetPasswordModal
        open={openResetPasswordOrgUserModal}
        onClose={handleModalsClose}
        onSubmit={handleResetPasswordOrgUserModalSubmit}
        submitInProgress={resetPasswordStatus.loading}
        submitErrors={resetPasswordStatus.errors}
      ></ResetPasswordModal>
    </>
  );
};

export default OrgUsersList;
